// ============================================================
// useCaseDeadlineAutoSave.ts
// Per-deadline debounced auto-save.
//
// Mirrors useWitnessAutoSave's shape verbatim — deadlines live
// in a list (per-case), and each row has its own pending timer
// and accumulated partial payload. A keystroke / completed-toggle
// on deadline A shouldn't reset deadline B's debounce window.
//
// Public API:
//   scheduleDeadlineSave(id, changes)  — debounce 1s, accumulate
//                                        changes across keystrokes,
//                                        fire one PATCH when idle.
//   flushDeadlineAutoSave()            — fire EVERY pending timer
//                                        immediately. Call before
//                                        navigating away.
//   cancelDeadlineSave(id)             — drop the pending timer and
//                                        payload for one deadline.
//                                        Used on delete so we don't
//                                        PATCH a row we just removed.
// ============================================================
import { updateCaseDeadlineApi, listCaseDeadlinesApi } from '../api/casevoApi'
import type { UpdateCaseDeadlinePayload, CaseDeadline } from '../types/deadlines.types'
import {
  getDeadlineBaseUpdatedAt, setDeadlineBaseUpdatedAt,
  getDeadlineBaseValues, setDeadlineBaseValues,
} from './saveBaseVersion'
import { serverChangedTouchedColumns, MAX_409_REAPPLY_ATTEMPTS } from './reconcile409'
import { useCasevoStore } from '../store/useCasevoStore'
import { deadlineScope, refusedField, useFieldRefusalStore } from './fieldRefusals'

// Locate the caseId whose deadline list contains this row, so a 409 re-fetch
// can call listCaseDeadlinesApi(caseId) (there's no deadline-by-id endpoint).
// Scans all loaded lists; null → can't re-fetch (fall back to the toast).
const findDeadlineCaseId = (deadlineId: string): string | null => {
  const map = useCasevoStore.getState().caseDeadlines
  for (const [caseId, list] of Object.entries(map)) {
    if (list.some((d) => d.id === deadlineId)) return caseId
  }
  return null
}

// Put ONE field of a loaded row back to the server's value (after the server
// refused a change to it). Uses the last-saved base values when held, else
// re-fetches the case's list. A newer queued edit to that field isn't
// overwritten. Best-effort.
const restoreDeadlineField = async (deadlineId: string, field: string): Promise<void> => {
  const caseId = findDeadlineCaseId(deadlineId)
  if (!caseId) return
  let saved: Record<string, unknown> | null = getDeadlineBaseValues(deadlineId)
  if (!saved || !(field in saved)) {
    try {
      const server = (await listCaseDeadlinesApi(caseId)).find((d) => d.id === deadlineId)
      saved = server ? (server as unknown as Record<string, unknown>) : null
    } catch { saved = null }
  }
  if (!saved || !(field in saved)) return
  if (field in (pendingPayloads.get(deadlineId) ?? {})) return
  const store = useCasevoStore.getState()
  const local = (store.caseDeadlines[caseId] ?? []).find((d) => d.id === deadlineId)
  if (local) store.updateCaseDeadline({ ...local, [field]: saved[field] } as CaseDeadline)
}

const DEBOUNCE_MS = 1000

// Collab (Yjs) columns on a deadline: `notes` is now a passive HTML MIRROR whose
// merge is owned by Yjs (doc `deadline:<id>:notes`, Stage 4). A 409 caused solely
// by such a column must NEVER toast. Mirrors CASE/WITNESS/TASK_COLLAB_COLUMNS.
const DEADLINE_COLLAB_COLUMNS = new Set<string>(['notes'])

// Module-scope state. Same singleton pattern as useWitnessAutoSave
// but keyed by deadlineId so each row debounces independently.
const timers = new Map<string, ReturnType<typeof setTimeout>>()
// Accumulated partial — every change merges into here. When the
// timer fires we PATCH with whatever has built up, then clear.
const pendingPayloads = new Map<string, UpdateCaseDeadlinePayload>()
// Rows whose last save FAILED (non-409) — this hook owns that save error.
const satelliteErrored = new Set<string>()

const runDeadlineSave = async (
  deadlineId: string,
  // Column-aware 409 recovery attempt counter (0 = first try).
  attempt: number = 0,
): Promise<void> => {
  const payload = pendingPayloads.get(deadlineId)
  if (!payload || Object.keys(payload).length === 0) return
  // Move the payload into a local before the await — if more
  // changes land during the request they'll start a new pending
  // entry rather than getting clobbered by this run's PATCH return.
  pendingPayloads.delete(deadlineId)
  // A field being sent again drops its old refusal note.
  for (const k of Object.keys(payload)) useFieldRefusalStore.getState().clearRefusal(deadlineScope(deadlineId), k)
  try {
    // Optimistic-concurrency: attach the SERVER updatedAt captured at load so
    // the backend can 409 on a stale write. Opt-in — omit if no base captured.
    const base = getDeadlineBaseUpdatedAt(deadlineId)
    const body = base !== null ? { ...payload, baseUpdatedAt: base } : payload
    const updated = await updateCaseDeadlineApi(deadlineId, body)
    // Advance the baseline to the server's new updatedAt.
    setDeadlineBaseUpdatedAt(deadlineId, updated.updatedAt)
    // Advance the column-aware base VALUES to the just-saved server row.
    setDeadlineBaseValues(deadlineId, updated as unknown as Record<string, unknown>)
    // Clear a save error THIS hook raised on an earlier failed attempt.
    if (satelliteErrored.delete(deadlineId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
  } catch (err) {
    const resp = (err as { response?: { status?: number; data?: { currentUpdatedAt?: string } } }).response
    if (resp?.status === 409) {
      // COLUMN-AWARE recovery (mirrors useAutoSave): re-fetch via the case's
      // deadline list and, if the server didn't change any of the columns THIS
      // save touched (vs our base values), silently re-apply on the fresh base —
      // no toast. Only a genuine same-column clash (or exhausted retries /
      // failed re-fetch) surfaces the conflict.
      const touched = Object.keys(payload)
      // Collab (Yjs) columns never drive a conflict — base both the overlap
      // decision and the toast on NON-collab columns; an all-collab 409 (just
      // the notes mirror) resolves silently.
      const nonCollabTouched = touched.filter((c) => !DEADLINE_COLLAB_COLUMNS.has(c))
      const baseValues = getDeadlineBaseValues(deadlineId)
      if (attempt < MAX_409_REAPPLY_ATTEMPTS && touched.length > 0 && baseValues) {
        const caseId = findDeadlineCaseId(deadlineId)
        if (caseId) {
          try {
            const list = await listCaseDeadlinesApi(caseId)
            const server = list.find((d) => d.id === deadlineId)
            if (server) {
              const overlap = serverChangedTouchedColumns(
                nonCollabTouched, baseValues, server as unknown as Record<string, unknown>, payload as Record<string, unknown>,
              )
              if (!overlap) {
                // Non-overlap: advance to the fresh server row and re-send ONLY
                // our columns. Re-stage the drained payload, MERGING any newer
                // change on top so we don't lose it.
                setDeadlineBaseUpdatedAt(deadlineId, server.updatedAt)
                setDeadlineBaseValues(deadlineId, server as unknown as Record<string, unknown>)
                const newer = pendingPayloads.get(deadlineId) ?? {}
                pendingPayloads.set(deadlineId, { ...payload, ...newer })
                await runDeadlineSave(deadlineId, attempt + 1)
                return
              }
            }
          } catch { /* re-fetch failed — fall through to the conflict path */ }
        }
      }
      // Genuine same-column conflict, retry cap hit, or re-fetch failed:
      // existing non-destructive behaviour — advance base from the 409 body, warn.
      if (resp.data?.currentUpdatedAt) setDeadlineBaseUpdatedAt(deadlineId, resp.data.currentUpdatedAt)
      // Only a genuine NON-collab conflict warns; an all-collab clash (just the
      // notes mirror) resolves silently — Yjs owns the merged content.
      if (nonCollabTouched.length === 0) return
      useCasevoStore.getState().setSaveConflict(
        "A deadline was updated by someone else. Your latest change wasn't saved — please review and re-enter it.",
      )
      // ★ (case-page audit) Replace the row's OPTIMISTIC value with the
      // server's — otherwise the unsaved date (e.g. a limitation date) kept
      // showing as if saved, contradicting the "wasn't saved" warning. The
      // notes mirror is kept (Yjs owns it); any newer queued edit still rides.
      const caseId = findDeadlineCaseId(deadlineId)
      if (caseId) {
        try {
          const list = await listCaseDeadlinesApi(caseId)
          const server = list.find((d) => d.id === deadlineId)
          const store = useCasevoStore.getState()
          const local = (store.caseDeadlines[caseId] ?? []).find((d) => d.id === deadlineId)
          if (server && local) {
            const newer = pendingPayloads.get(deadlineId) ?? {}
            store.updateCaseDeadline({ ...server, notes: local.notes, ...(newer as Partial<CaseDeadline>) })
            setDeadlineBaseUpdatedAt(deadlineId, server.updatedAt)
            setDeadlineBaseValues(deadlineId, server as unknown as Record<string, unknown>)
          }
        } catch { /* best-effort — the warning already tells the user to review */ }
      }
      return
    }
    // ★ 400 naming ONE field (e.g. a limitation date before the accident):
    // the row shows the SERVER's value for that field again, the server's
    // message shows under it (hooks/fieldRefusals), and the row's OTHER
    // changes are re-sent now. Before, the whole payload was dropped and the
    // refused date kept showing as if saved.
    const refused = refusedField(err)
    if (refused && refused.field in payload) {
      useFieldRefusalStore.getState().setRefusal(deadlineScope(deadlineId), refused.field, refused.message)
      if (satelliteErrored.delete(deadlineId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
      await restoreDeadlineField(deadlineId, refused.field)
      const rest = { ...payload } as Record<string, unknown>
      delete rest[refused.field]
      if (Object.keys(rest).length > 0) {
        pendingPayloads.set(deadlineId, { ...(rest as UpdateCaseDeadlinePayload), ...(pendingPayloads.get(deadlineId) ?? {}) })
        await runDeadlineSave(deadlineId, attempt)
      }
      return
    }
    // eslint-disable-next-line no-console
    console.error('[DeadlineAutoSave] Failed to save deadline:', deadlineId, err)
    // ★ Don't DROP the change (it was already removed from pending before the
    // request): re-queue it UNDER any newer edit so the next flush/keystroke
    // re-sends it, surface the save error, and retry once for transient
    // (network / 5xx) failures. 4xx = the server rejected the value — no
    // auto-retry loop; the error stays until the user edits it.
    const status = (err as { response?: { status?: number } }).response?.status
    if (status === 404) {
      // Row no longer exists (deleted elsewhere) — nothing to save; drop it
      // quietly rather than leaving a permanent error + leave-page prompt.
      if (satelliteErrored.delete(deadlineId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
      return
    }
    useCasevoStore.getState().setSaveError(err instanceof Error ? err : new Error('Save failed'))
    satelliteErrored.add(deadlineId)
    if (status !== undefined && status >= 400 && status < 500) {
      // The server REJECTED the value — re-sending the same payload can't
      // succeed, so don't re-queue it (it would pin the leave-page prompt).
      // The error stays until this row next saves successfully.
      return
    }
    // Transient (network / 5xx): re-queue under any newer edit so a flush or
    // "Try again" re-sends it, and auto-retry once.
    pendingPayloads.set(deadlineId, { ...payload, ...(pendingPayloads.get(deadlineId) ?? {}) })
    if (attempt === 0) {
      setTimeout(() => { void runDeadlineSave(deadlineId, 1) }, 5000)
    }
  }
}

// ── Stage 1c: apply a REMOTE deadline edit without a save-loop ────────
// Mirror of applyRemoteWitnessEdit — same reasoning: this hook has no
// whole-row baseline and no store subscription (saves fire only via
// scheduleDeadlineSave from onChange), so writing the store row directly
// triggers no save. Merge the remote field(s) into the specific ROW and
// advance THAT row's base to the server updatedAt from the message. No-ops if
// the row isn't loaded (late message after delete).
export const applyRemoteDeadlineEdit = (
  deadlineId: string,
  updates: Partial<CaseDeadline>,
  serverUpdatedAt: string,
): void => {
  const store = useCasevoStore.getState()
  let row: CaseDeadline | undefined
  for (const list of Object.values(store.caseDeadlines)) {
    const found = list.find((d) => d.id === deadlineId)
    if (found) { row = found; break }
  }
  if (!row) return
  const merged = { ...row, ...updates }
  store.updateCaseDeadline(merged)
  setDeadlineBaseUpdatedAt(deadlineId, serverUpdatedAt)
  // Keep the column-aware base VALUES aligned with the applied remote state.
  setDeadlineBaseValues(deadlineId, merged as unknown as Record<string, unknown>)
}

export const scheduleDeadlineSave = (
  deadlineId: string,
  changes: UpdateCaseDeadlinePayload,
): void => {
  // Merge with any partial that's already queued — fast typing
  // produces several `{ notes: '...' }` calls and we want them all
  // collapsed into one PATCH.
  const existing = pendingPayloads.get(deadlineId) ?? {}
  pendingPayloads.set(deadlineId, { ...existing, ...changes })
  // Reset the debounce window.
  const existingTimer = timers.get(deadlineId)
  if (existingTimer !== undefined) clearTimeout(existingTimer)
  const newTimer = setTimeout(() => {
    timers.delete(deadlineId)
    void runDeadlineSave(deadlineId)
  }, DEBOUNCE_MS)
  timers.set(deadlineId, newTimer)
}

// Cancel pending timer + payload for one deadline. Used on delete so
// we don't PATCH a row that's about to vanish from the backend.
export const cancelDeadlineSave = (deadlineId: string): void => {
  if (satelliteErrored.delete(deadlineId) && satelliteErrored.size === 0) useCasevoStore.getState().setSaveError(null)
  const timer = timers.get(deadlineId)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(deadlineId)
  }
  pendingPayloads.delete(deadlineId)
}

// Drain the pending payload for one deadline + clear its timer,
// returning the payload (or undefined if nothing was pending).
// Used by callers that need to fire an IMMEDIATE PATCH (e.g. the
// completed-checkbox toggle) and want to fold any buffered debounced
// changes into the same network roundtrip — avoids dropping a date
// or notes edit that hasn't fired yet. Distinct from cancelDeadlineSave
// (which silently drops the payload) — this one HANDS THE PAYLOAD BACK.
export const consumePendingDeadlinePayload = (
  deadlineId: string,
): UpdateCaseDeadlinePayload | undefined => {
  const timer = timers.get(deadlineId)
  if (timer !== undefined) {
    clearTimeout(timer)
    timers.delete(deadlineId)
  }
  const payload = pendingPayloads.get(deadlineId)
  pendingPayloads.delete(deadlineId)
  return payload
}

// (D12 live updates) Is any deadline edit still waiting to be saved? A live
// re-read of the list waits until there's none.
export const hasPendingDeadlineSaves = (): boolean => timers.size > 0 || pendingPayloads.size > 0

// Fire every pending deadline save immediately and await them all.
// Mirrors flushWitnessAutoSave for the case-wide debounce.
export const flushDeadlineAutoSave = async (): Promise<void> => {
  // Include re-queued payloads (a failed save waiting for retry) — not just
  // rows with a live debounce timer — so navigation / "Try again" sends them.
  const ids = Array.from(new Set([...Array.from(timers.keys()), ...Array.from(pendingPayloads.keys())]))
  for (const id of ids) {
    const timer = timers.get(id)
    if (timer !== undefined) clearTimeout(timer)
    timers.delete(id)
  }
  await Promise.all(ids.map((id) => runDeadlineSave(id)))
}

// beforeunload guard — mirrors the one in useWitnessAutoSave so a
// refresh or tab close mid-edit still prompts the user when there
// are deadline edits in the debounce window. Attached once when the
// module is first imported; the early return makes it a no-op when
// nothing's pending. Modern browsers ignore custom messages, so
// preventDefault + returnValue = '' is enough to surface the dialog.
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', (event: BeforeUnloadEvent) => {
    if (timers.size === 0 && pendingPayloads.size === 0) return
    event.preventDefault()
    event.returnValue = ''
  })
}
